import { useAtomValue } from "jotai";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation, useParams } from "react-router-dom";

import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { cn } from "~/components/ui/cn";
import { Rating } from "~/components/ui/rating";
import type { Rating as OrderRating } from "~/graphql/graphql.gen";
import type { BrandMatch } from "~/routes/root";
import { brandState } from "~/stores/brand";
import { relativeDate } from "~/utils/date";

type OrderReviewProps = {
  review: OrderRating;
};

const getInitials = (name: string) => {
  return name
    .split(" ")
    .map((n) => n[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();
};

type ProductsProps = {
  orderId?: string | null;
  products?:
    | {
        id: string;
        image: string;
      }[]
    | null;
};

const NB_PRODUCTS_TO_DISPLAY = 4;

const Products: React.FC<ProductsProps> = ({
  orderId,
  products,
}: ProductsProps) => {
  const { brandId = "" } = useParams<BrandMatch>();

  const diff = (products?.length ?? 0) - NB_PRODUCTS_TO_DISPLAY;

  return (
    <div className="flex w-2/5 flex-wrap justify-end space-x-0.5">
      {products?.slice(0, NB_PRODUCTS_TO_DISPLAY).map((product, index) => {
        return (
          <NavLink
            key={index}
            to={`/${brandId}/orders/${orderId ?? ""}`}
            className="active:opacity-80"
          >
            <img
              src={product.image}
              className="size-14 rounded border border-gray-300 object-cover"
              alt=""
            />
          </NavLink>
        );
      })}
      {(products?.length ?? 0) > NB_PRODUCTS_TO_DISPLAY ? (
        <NavLink
          to={`/${brandId}/orders/${orderId ?? ""}`}
          className="active:opacity-80"
        >
          <p className="size-14 rounded bg-gray-700 object-cover text-center text-xs font-bold uppercase leading-[56px] tracking-wider text-white">
            +{diff}
          </p>
        </NavLink>
      ) : null}
    </div>
  );
};

export const OrderReview: React.FC<OrderReviewProps> = ({ review }) => {
  const { search } = useLocation();
  const { brandId: id = "" } = useParams<BrandMatch>();
  const brand = useAtomValue(brandState);
  const { i18n, t } = useTranslation();

  return (
    <div className="py-6">
      <header
        className={cn("flex flex-row", {
          "-mb-6": !review.rate_message,
        })}
      >
        <div className="flex flex-1 flex-col">
          <Rating value={(review.rate ?? 0) + 1} />
          {review.rate_message ? (
            <p className="mb-4 mt-3 leading-5.5 text-gray-900">
              {review.rate_message}
            </p>
          ) : null}
        </div>
        <Products orderId={review.order_id} products={review.products} />
      </header>
      <div className="flex flex-row items-center space-x-2">
        <Avatar size="lg">
          <AvatarImage src={review.avatar ?? undefined} />
          <AvatarFallback>{getInitials(review.name ?? "")}</AvatarFallback>
        </Avatar>

        <div className="flex flex-col">
          <div className="mb-0.5 text-sm font-semibold capitalize text-gray-700">
            {review.name}
          </div>
          <p className="space-x-1 text-xs text-gray-500">
            <span>
              {relativeDate(
                new Date(review.rate_updated_at ?? new Date()),
                i18n.language === "fr" ? "fr" : "en",
                brand?.timezone ?? undefined,
              )}
            </span>
            <span>•</span>
            <NavLink
              to={`/${id}/orders/${review.order_id ?? ""}${search}`}
              className="text-green-900"
            >
              {t("rating.open_order")}
            </NavLink>
          </p>
        </div>
      </div>
    </div>
  );
};
